import React, { useCallback, useEffect, useState } from 'react';
import { showToast } from '../../../utils/showToast';
import { reporteCaja, reporteCajaCajeros, abrirReporteCajaPdf } from '../../../services/pagosService';

// OJO: nunca usar new Date().toISOString().slice(0,10) para "hoy" — toISOString()
// convierte a UTC, y en Ecuador (UTC-5) desde las 19:00 ya marca el dia siguiente.
const hoy = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

function fmt(n) {
  return `$${Number(n || 0).toFixed(2)}`;
}

function fmtFecha(dt) {
  if (!dt) return '—';
  const d = new Date(dt);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yy} ${hh}:${mi}`;
}

const COLORES_METODO = {
  efectivo:      { bg: '#d1fae5', text: '#065f46', icon: 'bi-cash'          },
  transferencia: { bg: '#dbeafe', text: '#1e40af', icon: 'bi-bank'          },
  deposito:      { bg: '#e0e7ff', text: '#3730a3', icon: 'bi-building'      },
  tarjeta:       { bg: '#fce7f3', text: '#9d174d', icon: 'bi-credit-card'   },
  cheque:        { bg: '#fef3c7', text: '#92400e', icon: 'bi-file-earmark'  },
  bonificacion:  { bg: '#f3e8ff', text: '#6b21a8', icon: 'bi-gift'          },
};

function colorMetodo(valor) {
  return COLORES_METODO[valor] || { bg: '#f1f5f9', text: '#334155', icon: 'bi-circle' };
}

export default function ReporteCajaView() {
  const [fechaDesde,  setFechaDesde]  = useState(hoy());
  const [fechaHasta,  setFechaHasta]  = useState(hoy());
  const [cajeroId,    setCajeroId]    = useState('');
  const [cajeros,     setCajeros]     = useState([]);
  const [datos,       setDatos]       = useState(null);
  const [cargando,    setCargando]    = useState(false);
  const [generandoPdf, setGenerandoPdf] = useState(false);

  // Cargar cajeros al montar
  useEffect(() => {
    reporteCajaCajeros()
      .then(data => setCajeros(data || []))
      .catch(() => {});
  }, []);

  // Cargar reporte del día al montar
  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const data = await reporteCaja({
        fecha_desde: fechaDesde,
        fecha_hasta:  fechaHasta,
        cajero_id:    cajeroId || undefined,
      });
      setDatos(data);
    } catch (err) {
      showToast('error', err?.message || 'Error al cargar el reporte.');
    } finally {
      setCargando(false);
    }
  }, [fechaDesde, fechaHasta, cajeroId]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { cargar(); }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    cargar();
  };

  const handlePdf = async () => {
    setGenerandoPdf(true);
    try {
      await abrirReporteCajaPdf({
        fecha_desde: fechaDesde,
        fecha_hasta:  fechaHasta,
        cajero_id:    cajeroId || undefined,
      });
    } catch (err) {
      showToast('error', err?.message || 'Error al generar el PDF.');
    } finally {
      setGenerandoPdf(false);
    }
  };

  return (
    <div className="container-fluid py-4" style={{ maxWidth: 1100 }}>

      {/* Encabezado */}
      <div className="d-flex align-items-center gap-3 mb-4">
        <div className="bg-success bg-opacity-10 rounded-3 p-3">
          <i className="bi bi-journal-text fs-3 text-success" />
        </div>
        <div>
          <h4 className="mb-0 fw-bold">Reporte de Caja</h4>
          <small className="text-muted">Resumen diario de pagos recibidos</small>
        </div>
      </div>

      {/* Filtros */}
      <form onSubmit={handleSubmit}>
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-body">
            <div className="row g-3 align-items-end">
              <div className="col-sm-auto">
                <label className="form-label fw-semibold small">Desde</label>
                <input
                  type="date"
                  className="form-control"
                  value={fechaDesde}
                  max={fechaHasta}
                  onChange={e => setFechaDesde(e.target.value)}
                />
              </div>
              <div className="col-sm-auto">
                <label className="form-label fw-semibold small">Hasta</label>
                <input
                  type="date"
                  className="form-control"
                  value={fechaHasta}
                  min={fechaDesde}
                  onChange={e => setFechaHasta(e.target.value)}
                />
              </div>
              <div className="col-sm-auto" style={{ minWidth: 200 }}>
                <label className="form-label fw-semibold small">Cajero</label>
                <select
                  className="form-select"
                  value={cajeroId}
                  onChange={e => setCajeroId(e.target.value)}
                >
                  <option value="">Todos los cajeros</option>
                  {cajeros.map(c => (
                    <option key={c.id} value={c.id}>{c.nombre}</option>
                  ))}
                </select>
              </div>
              <div className="col-sm-auto d-flex gap-2">
                <button type="submit" className="btn btn-primary" disabled={cargando}>
                  {cargando
                    ? <><span className="spinner-border spinner-border-sm me-1" />Buscando...</>
                    : <><i className="bi bi-search me-1" />Buscar</>}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={handlePdf}
                  disabled={generandoPdf || !datos}
                >
                  {generandoPdf
                    ? <><span className="spinner-border spinner-border-sm me-1" />PDF...</>
                    : <><i className="bi bi-printer me-1" />Imprimir</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>

      {datos && (
        <>
          {/* Resumen por método */}
          <div className="row g-3 mb-4">
            {datos.resumen.map(r => {
              const c = colorMetodo(r.metodo_valor);
              return (
                <div key={r.id} className="col-6 col-md-4 col-lg-3">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-body d-flex align-items-center gap-3">
                      <div
                        className="rounded-3 p-2 fs-4 d-flex align-items-center justify-content-center"
                        style={{ background: c.bg, color: c.text, width: 48, height: 48, flexShrink: 0 }}
                      >
                        <i className={`bi ${c.icon}`} />
                      </div>
                      <div className="overflow-hidden">
                        <div className="text-muted small text-truncate">{r.metodo_nombre}</div>
                        <div className="fw-bold fs-5" style={{ color: c.text }}>{fmt(r.total)}</div>
                        <div className="text-muted" style={{ fontSize: 11 }}>{r.cantidad} pago{r.cantidad !== 1 ? 's' : ''}</div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Total general */}
            <div className="col-6 col-md-4 col-lg-3">
              <div className="card border-0 shadow-sm h-100" style={{ background: '#1a56db' }}>
                <div className="card-body d-flex align-items-center gap-3">
                  <div
                    className="rounded-3 p-2 fs-4 d-flex align-items-center justify-content-center"
                    style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', width: 48, height: 48, flexShrink: 0 }}
                  >
                    <i className="bi bi-wallet2" />
                  </div>
                  <div>
                    <div className="small" style={{ color: 'rgba(255,255,255,0.75)' }}>Total cobrado</div>
                    <div className="fw-bold fs-5 text-white">{fmt(datos.totales.total)}</div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)' }}>
                      {datos.totales.cantidad} pago{datos.totales.cantidad !== 1 ? 's' : ''}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tabla de detalle */}
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white border-bottom py-3 d-flex align-items-center justify-content-between">
              <h6 className="mb-0 fw-semibold">Detalle de pagos</h6>
              <span className="badge bg-secondary">{datos.detalle.length}</span>
            </div>
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: 50 }} className="text-center">#</th>
                    <th style={{ width: 130 }}>Fecha / Hora</th>
                    <th>Estudiante</th>
                    <th style={{ width: 130 }}>Concepto</th>
                    <th style={{ width: 120 }}>Método</th>
                    <th style={{ width: 130 }}>Referencia</th>
                    <th style={{ width: 130 }}>Cajero</th>
                    <th style={{ width: 140 }}>Factura</th>
                    <th style={{ width: 90 }} className="text-end">Monto</th>
                  </tr>
                </thead>
                <tbody>
                  {datos.detalle.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="text-center text-muted py-4">
                        <i className="bi bi-inbox me-2" />
                        Sin pagos en este período
                      </td>
                    </tr>
                  ) : datos.detalle.map((p, i) => {
                    const c = colorMetodo(p.metodo_valor);
                    return (
                      <tr key={p.id}>
                        <td className="text-center text-muted small">{i + 1}</td>
                        <td className="small text-muted">{fmtFecha(p.fecha_pago)}</td>
                        <td>
                          <div className="fw-semibold small">{p.estudiante_nombre || '—'}</div>
                          {p.estudiante_cedula && (
                            <div className="text-muted" style={{ fontSize: 11 }}>{p.estudiante_cedula}</div>
                          )}
                        </td>
                        <td className="small">{p.concepto_nombre || '—'}</td>
                        <td>
                          <span
                            className="badge rounded-pill px-2"
                            style={{ background: c.bg, color: c.text, fontSize: 11 }}
                          >
                            <i className={`bi ${c.icon} me-1`} />
                            {p.metodo_nombre}
                          </span>
                        </td>
                        <td className="small font-monospace">{p.referencia_externa || '—'}</td>
                        <td className="small">{p.cajero_nombre || '—'}</td>
                        <td className="small font-monospace text-muted">
                          {p.contifico_numero_factura || '—'}
                        </td>
                        <td className="text-end fw-semibold">{fmt(p.monto)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                {datos.detalle.length > 0 && (
                  <tfoot className="table-light">
                    <tr>
                      <td colSpan={8} className="text-end fw-bold">Total</td>
                      <td className="text-end fw-bold text-primary">{fmt(datos.totales.total)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
